import React from 'react';
import { X, RotateCcw } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import {
  PlayModeSettings as PlayModeSettingsType,
  DEFAULT_PLAY_MODE_SETTINGS,
} from '../../../core/types/live-preview.types';
import { FPS_PRESETS } from '../../utils/device-presets';

interface PlayModeSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

type OnPlayBehavior = 'keep-layout' | 'maximize' | 'focus';

const ON_PLAY_BEHAVIORS: { value: OnPlayBehavior; label: string; description: string }[] = [
  {
    value: 'keep-layout',
    label: 'Keep Layout',
    description: 'Keep the current panel layout when playing',
  },
  {
    value: 'maximize',
    label: 'Maximize',
    description: 'Maximize the game view panel when playing',
  },
  {
    value: 'focus',
    label: 'Focus',
    description: 'Focus on the game view panel when playing',
  },
];

export const PlayModeSettings: React.FC<PlayModeSettingsProps> = ({
  isOpen,
  onClose,
}) => {
  const { playModeSettings, updatePlayModeSettings } = useLivePreviewStore();

  if (!isOpen) return null;

  const handleResetToDefaults = () => {
    updatePlayModeSettings(DEFAULT_PLAY_MODE_SETTINGS);
  };

  const handleCheckboxChange = (key: keyof PlayModeSettingsType) => {
    updatePlayModeSettings({
      [key]: !playModeSettings[key],
    });
  };

  const handleBehaviorChange = (value: OnPlayBehavior) => {
    updatePlayModeSettings({ onPlayBehavior: value });
  };

  const handleMaxFpsChange = (value: number) => {
    updatePlayModeSettings({ maxFps: value });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleOverlayClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
          <h2 className="text-lg font-semibold text-zinc-100">
            Play Mode Settings
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700 rounded transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* On Play Behavior */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-zinc-300">
              On Play Behavior
            </label>
            <div className="space-y-2">
              {ON_PLAY_BEHAVIORS.map((behavior) => (
                <label
                  key={behavior.value}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    playModeSettings.onPlayBehavior === behavior.value
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50'
                  )}
                >
                  <input
                    type="radio"
                    name="onPlayBehavior"
                    value={behavior.value}
                    checked={playModeSettings.onPlayBehavior === behavior.value}
                    onChange={() => handleBehaviorChange(behavior.value)}
                    className="mt-0.5 w-4 h-4 text-blue-500 bg-zinc-700 border-zinc-600 focus:ring-blue-500 focus:ring-offset-zinc-900"
                  />
                  <div>
                    <div className="text-sm font-medium text-zinc-200">
                      {behavior.label}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {behavior.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Playback Options */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-zinc-300">
              Playback Options
            </label>
            <div className="space-y-2">
              <CheckboxOption
                checked={playModeSettings.reloadScripts}
                onChange={() => handleCheckboxChange('reloadScripts')}
                label="Reload Scripts"
                description="Reload all scripts when entering play mode"
              />
              <CheckboxOption
                checked={playModeSettings.resetSceneState}
                onChange={() => handleCheckboxChange('resetSceneState')}
                label="Reset Scene State"
                description="Restore scene to original state when stopping"
              />
              <CheckboxOption
                checked={playModeSettings.pauseOnStart}
                onChange={() => handleCheckboxChange('pauseOnStart')}
                label="Pause on Start"
                description="Start in paused state for debugging"
              />
              <CheckboxOption
                checked={playModeSettings.stepFrameOnPause}
                onChange={() => handleCheckboxChange('stepFrameOnPause')}
                label="Enable Frame Stepping"
                description="Allow stepping through frames when paused"
              />
            </div>
          </div>

          {/* Performance Settings */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-zinc-300">
              Performance
            </label>
            <div className="space-y-3">
              {/* VSync Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-zinc-200">VSync</div>
                  <div className="text-xs text-zinc-500">
                    Synchronize with display refresh rate
                  </div>
                </div>
                <ToggleSwitch
                  checked={playModeSettings.vsyncEnabled}
                  onChange={() => handleCheckboxChange('vsyncEnabled')}
                />
              </div>

              {/* Max FPS Dropdown */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-zinc-200">Max FPS</div>
                  <div className="text-xs text-zinc-500">
                    Limit frame rate (0 = unlimited)
                  </div>
                </div>
                <select
                  value={playModeSettings.maxFps}
                  onChange={(e) => handleMaxFpsChange(Number(e.target.value))}
                  disabled={playModeSettings.vsyncEnabled}
                  className={cn(
                    'px-3 py-1.5 text-sm rounded-lg border transition-colors',
                    'bg-zinc-800 border-zinc-700 text-zinc-200',
                    'hover:border-zinc-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {FPS_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-700">
          <button
            onClick={handleResetToDefaults}
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors',
              'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
            )}
          >
            <RotateCcw size={14} />
            Reset to Defaults
          </button>
          <button
            onClick={onClose}
            className={cn(
              'px-4 py-1.5 text-sm font-medium rounded-lg transition-colors',
              'bg-blue-600 text-white hover:bg-blue-700',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-900'
            )}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper Components

interface CheckboxOptionProps {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
}

const CheckboxOption: React.FC<CheckboxOptionProps> = ({
  checked,
  onChange,
  label,
  description,
}) => (
  <label className="flex items-start gap-3 cursor-pointer group">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className={cn(
        'mt-0.5 w-4 h-4 rounded transition-colors',
        'bg-zinc-700 border-zinc-600 text-blue-500',
        'focus:ring-blue-500 focus:ring-offset-zinc-900'
      )}
    />
    <div>
      <div className="text-sm text-zinc-200 group-hover:text-zinc-100">
        {label}
      </div>
      <div className="text-xs text-zinc-500">{description}</div>
    </div>
  </label>
);

interface ToggleSwitchProps {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  disabled = false,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={cn(
      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-zinc-900',
      checked ? 'bg-blue-600' : 'bg-zinc-600',
      disabled && 'opacity-50 cursor-not-allowed'
    )}
  >
    <span
      className={cn(
        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
        checked ? 'translate-x-5' : 'translate-x-0'
      )}
    />
  </button>
);
